import {
  ContainerOutlined,
  FileAddOutlined,
  PlusOutlined,
  PrinterOutlined,
  RollbackOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import {
  InventorySelect,
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
  ServiceDetailsCard,
  ServiceSelect,
} from '@components';
import { useInventoryCategories } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Button, Popover, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { IOrderServiceSelectProps } from './OrderServiceSelect.props';
import { OrderServicePrintModal } from './OrderServicePrintModal';
import { ReturnPartsModal } from './ReturnPartsModal';

const { Text } = Typography;

const sectionStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f0f7ff 0%, #f8fbff 100%)',
  borderRadius: 10,
  border: '1px solid #d4e4f7',
  padding: '16px 16px 12px',
  marginBottom: 8,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: '#1a3353',
  letterSpacing: 0.2,
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  background: '#2CBDFF',
  color: '#fff',
  fontSize: 14,
};

const OrderServiceSelectPresentation: React.FC<IOrderServiceSelectProps> = ({
  atLeastOneService,
  disabled,
  variant = 'selected',
  basket,
  hideSavedOnlyActions,
}) => {
  const { serviceStore, authenticationStore, orderStore } = useStore();
  const isTechnician = authenticationStore.currentUser?.role === 'Technician';
  const currentOrderId = orderStore.order?.id;
  const { options: categoryOptions } = useInventoryCategories();
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const isAdditional = variant === 'additional';
  const isRevision = variant === 'revision';
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [returnPartsOpen, setReturnPartsOpen] = useState(false);
  const isMainVariant = !isAdditional && !isRevision;
  // A basket has no additional/revision sets, so the variant switching below is bypassed entirely.
  const isBasket = !!basket;

  const handleUploadSuccess = (createdItems: any[], parsed?: any[]) => {
    if (basket) {
      basket.addPartsFromInventoryBulk(createdItems, parsed);
      return;
    }
    serviceStore.addPartsFromInventoryBulk(
      createdItems,
      parsed,
      isAdditional ? 'additional' : isRevision ? 'revision' : 'selected'
    );
  };

  const services = basket
    ? basket.selectedServices
    : isAdditional
      ? serviceStore.additionalSelectedServices
      : isRevision
        ? serviceStore.revisionSelectedServices
        : serviceStore.selectedServices;

  const partsMap = basket
    ? basket.selectedServiceParts
    : isAdditional
      ? serviceStore.additionalServiceParts
      : isRevision
        ? serviceStore.revisionServiceParts
        : serviceStore.selectedServiceParts;

  const dispatchParts = basket
    ? basket.dispatchSelectedServiceParts
    : isAdditional
      ? serviceStore.dispatchAdditionalServiceParts
      : isRevision
        ? serviceStore.dispatchRevisionServiceParts
        : serviceStore.dispatchSelectedServiceParts;

  const tenantVat = basket ? basket.tenantVat : serviceStore.tenantVat;

  const pricingSummary = basket
    ? basket.pricingSummary
    : isRevision
      ? serviceStore.revisionPricingSummary
      : serviceStore.pricingSummary;

  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  // Keep active tab in sync: select the last service whenever the active tab is
  // missing or points to a service that no longer exists (e.g. after a revise
  // re-hydrates the list with the same count but replaced entries).
  const serviceIdsKey = services.map((s) => s.id).join(',');
  useEffect(() => {
    if (services.length === 0) {
      setActiveTab(undefined);
    } else if (!activeTab || !services.some((s) => String(s.id) === activeTab)) {
      setActiveTab(String(services[services.length - 1].id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceIdsKey]);

  // For the order being edited, keep its end time anchored to the total service duration.
  // A basket has no saved order behind it, so there is nothing to anchor.
  const syncOrderEndTime = () => {
    if (isBasket || variant !== 'selected') return;
    const totalHours = serviceStore.selectedServices.reduce((acc, s) => acc + (s.duration || 0), 0);
    orderStore.syncEndFromServiceDuration(totalHours);
  };

  const handleSelect = (id: number) => {
    if (basket) basket.addService(id);
    else if (isAdditional) serviceStore.addAdditionalService(id);
    else if (isRevision) serviceStore.addRevisionService(id);
    else serviceStore.addService(id);
    setActiveTab(String(id));
    syncOrderEndTime();
  };

  const handleDeselect = (id: number) => {
    if (basket) basket.removeService(id, atLeastOneService);
    else if (isAdditional) serviceStore.removeAdditionalService(id);
    else if (isRevision) serviceStore.removeRevisionService(id, atLeastOneService);
    else serviceStore.removeService(id, atLeastOneService);
    syncOrderEndTime();
  };

  const handleClear = () => {
    if (basket) basket.clearSelectedServices();
    else if (isAdditional) serviceStore.clearAdditionalSelectedServices();
    else if (isRevision) serviceStore.clearRevisionSelectedServices();
    else serviceStore.clearSelectedServices();
    syncOrderEndTime();
  };

  return (
    <div style={sectionStyle}>
      <div style={{ ...sectionHeaderStyle, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={sectionTitleStyle}>Services</Text>
          {services.length > 0 && (
            <>
              <Badge
                count={services.length}
                style={{
                  backgroundColor: '#2CBDFF',
                  fontSize: 11,
                  fontWeight: 600,
                  boxShadow: '0 1px 4px rgba(44, 189, 255, 0.3)',
                }}
              />
            </>
          )}
        </div>
        {!isAdditional && services.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: 14,
              alignItems: 'center',
              background: '#fff',
              border: '1px solid #d4e4f7',
              borderRadius: 8,
              padding: '8px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <span style={{ color: '#555' }}>
              Labour:{' '}
              <strong style={{ color: '#1a3353' }}>
                {Utils.formatCurrency(pricingSummary.labourPrice)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              Parts:{' '}
              <strong style={{ color: '#1a3353' }}>
                {Utils.formatCurrency(pricingSummary.partsPrice)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              VAT:{' '}
              <strong style={{ color: '#1a3353' }}>
                {Utils.formatCurrency(pricingSummary.vatAmount)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <Tag
              color="blue"
              style={{ fontSize: 15, fontWeight: 700, padding: '2px 12px', margin: 0 }}
            >
              {Utils.formatCurrency(pricingSummary.totalPrice)}
            </Tag>
          </div>
        )}
      </div>

      <div style={{ marginBottom: services.length > 0 ? 16 : 0 }}>
        <ServiceSelect
          mode="multiple"
          showMyServicesOnly
          placement="topLeft"
          allowClear={!atLeastOneService}
          value={services.map((x) => x.id)}
          defaultValue={services.map((x) => ({
            label: Utils.formatServiceLabel(x),
            value: x.id,
          }))}
          onClear={handleClear}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          disabled={disabled}
        />
      </div>

      {services.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: -1 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {services.map((s) => {
                const isActive = activeTab === String(s.id);
                const sParts = partsMap.get(s.id) || [];
                const sSubTotal = Utils.calculateLabourPrice(s);
                const sVatAmount = s.isVat
                  ? Utils.roundToTwoDecimals((sSubTotal * tenantVat) / 100)
                  : 0;
                const sPartsPrice = Utils.calculatePartsPrice(sParts);
                const sPartsVat = sParts.reduce(
                  (acc, p) =>
                    acc + Utils.calculateVat({ ...p, duration: p.quantity, vat: tenantVat }),
                  0
                );
                const tabTotal = Utils.roundToTwoDecimals(
                  sSubTotal + sVatAmount + sPartsPrice + sPartsVat
                );
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveTab(String(s.id))}
                    style={{
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#fff' : '#1a3353',
                      background: isActive ? '#2CBDFF' : '#fff',
                      border: isActive ? '1px solid #2CBDFF' : '1px solid #d4e4f7',
                      borderBottom: isActive ? '1px solid #fff' : '1px solid #d4e4f7',
                      borderRadius: '8px 8px 0 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      zIndex: isActive ? 1 : 0,
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      textAlign: 'center',
                      minWidth: 120,
                    }}
                  >
                    <ToolOutlined style={{ marginRight: 6, fontSize: 12 }} />
                    {Utils.formatServiceLabel(s)}
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        marginTop: 2,
                        color: isActive ? 'rgba(255,255,255,0.85)' : '#096dd9',
                      }}
                    >
                      {Utils.formatCurrency(tabTotal)}
                      {s.isVat && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: 9,
                            fontWeight: 600,
                            opacity: 0.75,
                          }}
                        >
                          INC VAT
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            {!disabled && activeTab && (
              /* Fill, not colour, is what separates these. Five coloured buttons in a row read as
                  a wall and nothing leads; Add extra is the action this row exists for, so it is the
                  only filled one and the rest are plain. */
              <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    dispatchParts({ type: 'ADD_SERVICE_PART', serviceId: Number(activeTab) })
                  }
                >
                  Add extra
                </Button>
                <Popover
                  trigger="click"
                  placement="topRight"
                  content={
                    <div style={{ width: 650 }}>
                      <InventorySelect
                        onSelect={(item) => {
                          dispatchParts({
                            type: 'ADD_SERVICE_PART_FROM_INVENTORY',
                            serviceId: Number(activeTab),
                            servicePart: {
                              name: item.name,
                              number: item.partNumber,
                              reference: item.reference,
                              costPrice: item.costPrice,
                              price: item.sellPrice,
                              quantity: 1,
                              margin:
                                item.sellPrice > 0
                                  ? ((item.sellPrice - item.costPrice) / item.sellPrice) * 100
                                  : 0,
                              type: item.categoryName || 'Part',
                              inventoryId: item.id,
                              availableStock: item.quantity,
                              required: false,
                            },
                          });
                        }}
                      />
                    </div>
                  }
                >
                  <Button icon={<ContainerOutlined />}>From Inventory</Button>
                </Popover>
                {!isTechnician && (
                  <Button
                    icon={<FileAddOutlined />}
                    onClick={() =>
                      uploadDrawerRef.current?.open({
                        orderId: currentOrderId,
                        serviceOptions: services.map((s) => ({
                          id: s.id,
                          name: Utils.formatServiceLabel(s),
                        })),
                      })
                    }
                  >
                    Upload parts
                  </Button>
                )}
                {/* Returns run against a saved order's extras, so they are meaningless for a job
                    that has not been created yet. */}
                {isMainVariant && !isTechnician && !isBasket && !hideSavedOnlyActions && (
                  <Button icon={<RollbackOutlined />} onClick={() => setReturnPartsOpen(true)}>
                    Return parts
                  </Button>
                )}
                <Button icon={<PrinterOutlined />} onClick={() => setPrintModalOpen(true)}>
                  Prints
                </Button>
              </div>
            )}
          </div>
          <div
            style={{
              border: '1px solid #d4e4f7',
              borderRadius: '0 8px 8px 8px',
              background: '#fff',
              padding: 4,
            }}
          >
            {services
              .filter((s) => String(s.id) === activeTab)
              .map((s) => (
                <ServiceDetailsCard
                  key={s.id}
                  service={s}
                  disabled={disabled}
                  variant={variant}
                  basket={basket}
                />
              ))}
          </div>
        </>
      )}
      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={handleUploadSuccess}
      />
      <OrderServicePrintModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        services={services}
        partsMap={partsMap}
        tenantVat={tenantVat}
        orderTitle={orderStore.order?.title}
        registrationNumber={orderStore.order?.registrationNumber}
        customerName={orderStore.order?.customer?.fullname}
        mileage={orderStore.order?.mileage}
      />
      <ReturnPartsModal open={returnPartsOpen} onClose={() => setReturnPartsOpen(false)} />
    </div>
  );
};

export default observer(OrderServiceSelectPresentation);
